import { useEffect, useMemo, useRef, useState } from "react";

import ScheduleActivityTable from "./ScheduleActivityTable";
import ScheduleSummary from "./ScheduleSummary";
import ScheduleSourceStatus from "../../schedule/ScheduleSourceStatus";

const filters = [
  ["needs_attention", "Precisa de atuação"],
  ["critical", "Críticas"],
  ["delayed", "Atrasadas"],
  ["next_six_weeks", "Próximas 6 semanas"],
  ["milestones", "Marcos"],
  ["no_measurement", "Sem medição"],
  ["on_track", "No caminho"],
  ["all", "Todas"],
];

const stateMessage = (data) => {
  if (data?.status === "disabled") {
    return "A análise detalhada está desativada neste ambiente.";
  }
  if (data?.status === "inconsistent") {
    return "Os dados do cronograma não passaram pela validação de identidade.";
  }
  return "A análise de atividades ainda não foi sincronizada para esta obra.";
};

const PlannedMeasuredPanel = ({
  wbs,
  scheduleState = {},
  onRequest,
  graph,
  sourceMetadata,
  beginAt,
  endAt,
}) => {
  const [status, setStatus] = useState("needs_attention");
  const [groupBy, setGroupBy] = useState("activity");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const onRequestRef = useRef(onRequest);
  const limit = 20;
  const beginAtTime = beginAt?.getTime?.();
  const endAtTime = endAt?.getTime?.();
  const request = useMemo(() => ({
    wbs,
    page,
    limit,
    status,
    groupBy,
    search,
    beginAt: Number.isFinite(beginAtTime) ? new Date(beginAtTime) : undefined,
    endAt: Number.isFinite(endAtTime) ? new Date(endAtTime) : undefined,
  }), [wbs, page, status, groupBy, search, beginAtTime, endAtTime]);

  useEffect(() => {
    onRequestRef.current = onRequest;
  }, [onRequest]);

  useEffect(() => {
    if (wbs && onRequestRef.current) onRequestRef.current(request);
  }, [request, wbs]);

  const selectStatus = (nextStatus) => {
    setPage(1);
    setStatus(nextStatus);
  };
  const { data, loading, error } = scheduleState;

  return (
    <section className="planned-measured-panel">
      <div className="planned-measured-graph" aria-label="Gráfico mensal">
        {graph}
      </div>

      <ScheduleSourceStatus metadata={sourceMetadata} compact />

      {loading && <p className="schedule-callout">Analisando o cronograma…</p>}
      {!loading && error && (
        <p className="schedule-callout schedule-callout-error">{error}</p>
      )}
      {!loading && !error && data?.available === false && (
        <p className="schedule-callout">{stateMessage(data)}</p>
      )}

      {!loading && !error && data?.available && (
        <div className="planned-measured-analysis">
          {data.status === "stale" && (
            <p className="schedule-callout schedule-callout-warning">
              A sincronização mais recente falhou. Exibindo a última análise válida.
            </p>
          )}
          <div className="schedule-meta">
            <div>
              <span>Fonte</span>
              <strong>{data.source === "prevision" ? "Prevision" : "—"}</strong>
            </div>
            <div>
              <span>Sincronizado em</span>
              <strong>
                {data.syncedAt
                  ? new Date(data.syncedAt).toLocaleString("pt-BR")
                  : "—"}
              </strong>
            </div>
            <div>
              <span>Cobertura</span>
              <strong>
                {Number.isInteger(data.coverage?.linked) && Number.isInteger(data.coverage?.eligible)
                  ? `${data.coverage.linked}/${data.coverage.eligible}`
                  : "—"}
                {Number.isFinite(data.coverage?.percentage)
                  ? ` · ${String(data.coverage.percentage).replace(".", ",")}%`
                  : ""}
              </strong>
            </div>
          </div>

          <ScheduleSummary
            summary={data.summary}
            activeStatus={status}
            onStatus={selectStatus}
          />

          <div className="schedule-toolbar">
            <div className="schedule-filter-strip" aria-label="Filtros de atividades">
              {filters.map(([key, label]) => (
                <button
                  type="button"
                  key={key}
                  className={status === key ? "active" : ""}
                  aria-pressed={status === key}
                  onClick={() => selectStatus(key)}
                >
                  {label}
                </button>
              ))}
            </div>
            <div className="schedule-search-group">
              <label>
                <span>Buscar atividade</span>
                <input
                  type="search"
                  value={search}
                  onChange={(event) => {
                    setPage(1);
                    setSearch(event.target.value);
                  }}
                  placeholder="Serviço, pavimento ou código"
                />
              </label>
              <label>
                <span>Agrupar por</span>
                <select
                  value={groupBy}
                  onChange={(event) => {
                    setPage(1);
                    setGroupBy(event.target.value);
                  }}
                >
                  <option value="activity">Atividade</option>
                  <option value="service">Serviço</option>
                  <option value="floor">Pavimento</option>
                </select>
              </label>
            </div>
          </div>

          <ScheduleActivityTable rows={data.rows} />

          {(data.pagination?.totalPages ?? 0) > 1 && (
            <nav className="schedule-pagination" aria-label="Paginação de atividades">
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => setPage((current) => Math.max(1, current - 1))}
              >
                Anterior
              </button>
              <span>
                Página {data.pagination.page} de {data.pagination.totalPages}
              </span>
              <button
                type="button"
                disabled={page >= data.pagination.totalPages}
                onClick={() => setPage((current) => current + 1)}
              >
                Próxima
              </button>
            </nav>
          )}
        </div>
      )}
    </section>
  );
};

export default PlannedMeasuredPanel;
